import { differenceInYears } from 'date-fns'
import { TFunction } from 'i18next'

import { ClientProfileResponse } from '@/api/clients/get-client-profile'
import { ZIP_CODE_CA_REGEX, ZIP_CODE_US_REGEX } from '@/constants/regex'
import { EMPTY_VALUE } from '@/constants/ui'
import { DirtyFieldChange } from '@/hooks'
import { formatDateOnly } from '@/utils/date'

import { GENERAL_INFO_FIELD_LABELS } from './general-information.constants'

export const formatDateOfBirth = (dateStr: string | null, t: TFunction<['clients']>): string => {
  if (!dateStr) return EMPTY_VALUE
  const date = new Date(dateStr)
  const age = differenceInYears(new Date(), date)
  return t('clients:generalInformation.dateOfBirth.ageSuffix', {
    age,
    date: formatDateOnly(date),
  })
}

export const formatBooleanValue = (value: boolean, t: TFunction<['clients', 'common']>): string =>
  value ? t('common:booleans.yes') : t('common:booleans.no')

// US ZIPs are saved exactly as entered; Canadian postal codes are normalized to
// "A1A 1A1" (uppercase, single space) regardless of input case/spacing.
export const normalizeZipCode = (value: string): string => {
  if (ZIP_CODE_US_REGEX.test(value)) return value
  if (!ZIP_CODE_CA_REGEX.test(value)) return value

  const compact = value.replace(/\s/g, '').toUpperCase()
  return `${compact.slice(0, 3)} ${compact.slice(3)}`
}

export type PmsIdInfo = {
  pmsName: string
  pmsExternalId: string
}

const formatDirtyFieldValue = (value: unknown): string | null => {
  if (value instanceof File) return value.name
  if (value === null || value === undefined || value === '') return EMPTY_VALUE
  if (Array.isArray(value)) return null
  if (value instanceof Date) return formatDateOnly(value)
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

const computeArrayDiff = (
  prev: unknown,
  next: unknown,
  resolver?: (id: string) => string,
): {
  prevItems: { value: string; removed: boolean }[]
  nextItems: { value: string; added: boolean }[]
} | null => {
  if (!Array.isArray(prev) || !Array.isArray(next)) return null
  if (prev.some((v) => typeof v === 'object' && v !== null)) return null
  if (next.some((v) => typeof v === 'object' && v !== null)) return null

  const prevStrs = (prev as (string | number)[]).map(String)
  const nextStrs = (next as (string | number)[]).map(String)
  const prevSet = new Set(prevStrs)
  const nextSet = new Set(nextStrs)

  const prevItems = prevStrs.map((v) => ({ removed: !nextSet.has(v), value: resolver?.(v) ?? v }))
  const nextItems = nextStrs.map((v) => ({ added: !prevSet.has(v), value: resolver?.(v) ?? v }))

  const hasChanges = prevItems.some((i) => i.removed) || nextItems.some((i) => i.added)
  if (!hasChanges) return null

  return { nextItems, prevItems }
}

export const computeDirtyFieldChanges = (
  dirtyFields: Record<string, boolean | undefined>,
  defaultValues: Record<string, unknown>,
  currentValues: Record<string, unknown>,
  labelResolvers?: Partial<Record<string, (id: string) => string>>,
): DirtyFieldChange[] =>
  Object.entries(dirtyFields)
    .filter(([key, dirty]) => dirty && key in GENERAL_INFO_FIELD_LABELS)
    .flatMap(([key]): DirtyFieldChange[] => {
      const label = GENERAL_INFO_FIELD_LABELS[key]
      if (!label) return []

      const resolver = labelResolvers?.[key]
      const prevRaw = defaultValues[key]
      const nextRaw = currentValues[key]

      const arrayDiff = computeArrayDiff(prevRaw, nextRaw, resolver)
      if (Array.isArray(prevRaw) || Array.isArray(nextRaw)) {
        if (!arrayDiff) return []
        return [{ arrayDiff, label, nextValue: '', prevValue: '' }]
      }

      const prevValue = formatDirtyFieldValue(prevRaw)
      const nextValue = formatDirtyFieldValue(nextRaw)
      if (prevValue === null || nextValue === null) return []

      const resolveScalar = (raw: unknown, formatted: string) => {
        if (!resolver || formatted === EMPTY_VALUE || raw instanceof File) return formatted
        return resolver(String(raw))
      }

      const imageUrl = nextRaw instanceof File ? URL.createObjectURL(nextRaw) : undefined
      return [
        {
          imageUrl,
          label,
          nextValue: resolveScalar(nextRaw, nextValue),
          prevValue: resolveScalar(prevRaw, prevValue),
        },
      ]
    })

export const getPmsIdInfo = (clientData: ClientProfileResponse): PmsIdInfo | null => {
  if (clientData.yardiId) return { pmsExternalId: clientData.yardiId, pmsName: 'Yardi' }
  if (clientData.resmanId) return { pmsExternalId: clientData.resmanId, pmsName: 'ResMan' }
  if (clientData.realPageId)
    return { pmsExternalId: String(clientData.realPageId), pmsName: 'RealPage' }
  if (clientData.entrataId)
    return { pmsExternalId: String(clientData.entrataId), pmsName: 'Entrata' }
  if (clientData.rentManagerId)
    return { pmsExternalId: String(clientData.rentManagerId), pmsName: 'RentManager' }
  return null
}
